
import React, { useState, useRef, useEffect } from 'react';
import { ProjectData, AppendixImage, RabEntry, CatalogSuggestion, HazardFinding } from '../../types';
import { analyzeHazardImage, analyzeHazardSpot } from '../../services/geminiService';
import { ImagePlus, Trash2, Sparkles, Loader2, Target, PlusCircle, Check, X, ImageIcon, Move, LayoutGrid, Wand2 } from 'lucide-react';

interface AppendixManagerProps {
  projectData: ProjectData;
  entries: RabEntry[];
  onUpdate: (images: AppendixImage[]) => void;
  onAddEntry: (entry: Omit<RabEntry, 'id'>) => void;
}

// Safe ID generator fallback
const generateId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'id-' + Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 9);
};

export const AppendixManager: React.FC<AppendixManagerProps> = ({ projectData, entries, onUpdate, onAddEntry }) => {
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [analyzingFindingId, setAnalyzingFindingId] = useState<string | null>(null);
  const [activeFindingId, setActiveFindingId] = useState<string | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  
  // Dragging State
  const [isDragging, setIsDragging] = useState(false);
  const [draggedFindingId, setDraggedFindingId] = useState<string | null>(null);
  const [draggedImageId, setDraggedImageId] = useState<string | null>(null);
  
  // Ref to store references to the image container DOM elements
  const imageRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const images = projectData.appendixImages || [];

  // Auto-select first image if none selected
  useEffect(() => {
    if (!selectedImageId && images.length > 0) {
        setSelectedImageId(images[0].id);
    }
  }, [images.length]);

  // --- 1. IMAGE UPLOAD & COMPRESSION ---
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newImages: AppendixImage[] = [];
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        if (!file.type.startsWith('image/')) continue;
        try {
          const base64 = await compressImage(file);
          newImages.push({
            id: generateId(),
            imageData: base64,
            findings: [],
            suggestions: []
          });
        } catch (err) {
          console.error(err);
        }
      }
      
      if (newImages.length > 0) {
        onUpdate([...images, ...newImages]);
        // Select the first new image to show it immediately
        setSelectedImageId(newImages[0].id);
      }
      // Reset input
      e.target.value = '';
    }
  };

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          // Höhere Auflösung + Qualität: für die KI-Gefahrenerkennung sind Details
          // (Geländer, Risse, PSA, Kennzeichnungen) entscheidend. 1568px ist die von
          // Gemini empfohlene Kantenlänge, bei der das Bild nicht weiter herunterskaliert wird.
          const MAX_SIZE = 1568;
          let width = img.width;
          let height = img.height;
          if (width > height) {
             if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
          } else {
             if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
          } else { reject(new Error("Canvas Error")); }
        };
      };
      reader.onerror = reject;
    });
  };

  // Helper to crop image around a point (center of the box)
  const cropImageSpot = (base64Image: string, box: { xmin: number, xmax: number, ymin: number, ymax: number }): Promise<string> => {
    return new Promise((resolve, reject) => {
       const img = new Image();
       if (base64Image && base64Image.startsWith('http')) {
         img.crossOrigin = 'anonymous';
       }
       img.src = base64Image;
       img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) { reject("No ctx"); return; }

          // Tatsächliche Marker-Box (Prozent → Pixel). Statt eines starren 25%-Crops
          // schneiden wir genau den markierten Bereich aus, plus etwas Kontext-Padding,
          // damit die KI das Umfeld der Gefahrenstelle mitsieht.
          const boxXminPx = img.width * (Math.min(box.xmin, box.xmax) / 100);
          const boxXmaxPx = img.width * (Math.max(box.xmin, box.xmax) / 100);
          const boxYminPx = img.height * (Math.min(box.ymin, box.ymax) / 100);
          const boxYmaxPx = img.height * (Math.max(box.ymin, box.ymax) / 100);

          const boxW = Math.max(1, boxXmaxPx - boxXminPx);
          const boxH = Math.max(1, boxYmaxPx - boxYminPx);

          // 40 % Padding rundherum, Crop aber mindestens 250 px Kantenlänge.
          const padX = boxW * 0.4;
          const padY = boxH * 0.4;
          let cropW = Math.max(250, boxW + padX * 2);
          let cropH = Math.max(250, boxH + padY * 2);
          cropW = Math.min(cropW, img.width);
          cropH = Math.min(cropH, img.height);

          const cx = (boxXminPx + boxXmaxPx) / 2;
          const cy = (boxYminPx + boxYmaxPx) / 2;

          let sx = cx - (cropW / 2);
          let sy = cy - (cropH / 2);

          // Clamp bounds
          if (sx < 0) sx = 0;
          if (sy < 0) sy = 0;
          if (sx + cropW > img.width) sx = img.width - cropW;
          if (sy + cropH > img.height) sy = img.height - cropH;

          canvas.width = cropW;
          canvas.height = cropH;

          ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, cropW, cropH);
          resolve(canvas.toDataURL('image/jpeg', 0.9));
       };
       img.onerror = reject;
    });
  };

  // --- 2. DATA MANIPULATION ---
  
  const handleDeleteImage = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm("Bild wirklich löschen?")) {
      if (analyzingId === id) setAnalyzingId(null);
      const updated = images.filter(img => img.id !== id);
      onUpdate(updated);
      
      if (selectedImageId === id) {
          setSelectedImageId(updated.length > 0 ? updated[0].id : null);
      }
    }
  };

  const handleAiAnalysis = async (img: AppendixImage) => {
    setAnalyzingId(img.id);
    try {
      const result = await analyzeHazardImage(img.imageData, entries);
      const updated = images.map(i => {
        if (i.id === img.id) {
          return {
            ...i,
            findings: result.findings || [],
            suggestions: result.suggestions || []
          };
        }
        return i;
      });
      onUpdate(updated);
      if ((result.findings || []).length === 0) {
        alert("Die KI konnte auf diesem Bild keine eindeutigen Gefährdungen erkennen. Sie können manuell Marker setzen oder ein schärferes/näheres Foto verwenden.");
      }
    } catch (err) {
      console.error(err);
      alert("Fehler bei der KI-Analyse: " + (err instanceof Error ? err.message : "Unbekannter Fehler"));
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleAnalyzeSpot = async (img: AppendixImage, finding: HazardFinding) => {
     setAnalyzingFindingId(finding.id);
     try {
       const cropBase64 = await cropImageSpot(img.imageData, finding.box);
       const result = await analyzeHazardSpot(cropBase64);
       
       if (result) {
         const updated = images.map(i => {
            if (i.id === img.id) {
                return {
                    ...i,
                    findings: i.findings.map(f => f.id === finding.id ? { ...f, title: result.title, description: result.description } : f)
                };
            }
            return i;
         });
         onUpdate(updated);
       }
     } catch (err) {
       console.error(err);
       alert("Fehler bei der Detail-Analyse.");
     } finally {
       setAnalyzingFindingId(null);
     }
  };

  const handleAddManualFinding = (imgId: string) => {
    const newId = generateId();
    const updated = images.map(img => {
      if (img.id === imgId) {
        return {
          ...img,
          findings: [...img.findings, {
            id: newId,
            title: "",
            description: "",
            // Default to 50% / 50%
            box: { xmin: 45, xmax: 55, ymin: 45, ymax: 55 }
          }]
        };
      }
      return img;
    });
    onUpdate(updated);
    setActiveFindingId(newId);
  };

  const handleDeleteFinding = (imgId: string, findingId: string) => {
    const updated = images.map(img => {
      if (img.id === imgId) {
        return {
          ...img,
          findings: img.findings.filter(f => f.id !== findingId)
        };
      }
      return img;
    });
    onUpdate(updated);
  };

  const handleUpdateFindingText = (imgId: string, findingId: string, field: 'title'|'description', val: string) => {
    const updated = images.map(img => {
      if (img.id === imgId) {
        return {
          ...img,
          findings: img.findings.map(f => f.id === findingId ? { ...f, [field]: val } : f)
        };
      }
      return img;
    });
    onUpdate(updated);
  };

  const handleAcceptSuggestion = (imgId: string, sug: CatalogSuggestion, idx: number) => {
    onAddEntry(sug);
    const updated = images.map(img => {
      if (img.id === imgId && img.suggestions) {
        return { ...img, suggestions: img.suggestions.filter((_, i) => i !== idx) };
      }
      return img;
    });
    onUpdate(updated);
  };

  const handleIgnoreSuggestion = (imgId: string, idx: number) => {
    const updated = images.map(img => {
      if (img.id === imgId && img.suggestions) {
        return { ...img, suggestions: img.suggestions.filter((_, i) => i !== idx) };
      }
      return img;
    });
    onUpdate(updated);
  };

  // --- 3. DRAG AND DROP LOGIC (Global Listeners) ---

  const startDrag = (e: React.MouseEvent, imgId: string, findingId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDraggedImageId(imgId);
    setDraggedFindingId(findingId);
    setActiveFindingId(findingId);
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging || !draggedImageId || !draggedFindingId) return;

      const container = imageRefs.current[draggedImageId];
      if (!container) return;

      const rect = container.getBoundingClientRect();
      
      // Calculate position relative to the image container
      let x = e.clientX - rect.left;
      let y = e.clientY - rect.top;

      // Convert to percentages
      let perX = (x / rect.width) * 100;
      let perY = (y / rect.height) * 100;

      // Clamp to 0-100
      perX = Math.max(0, Math.min(100, perX));
      perY = Math.max(0, Math.min(100, perY));

      // Update State
      const updated = images.map(img => {
        if (img.id === draggedImageId) {
          return {
            ...img,
            findings: img.findings.map(f => {
              if (f.id === draggedFindingId) {
                // Keep the box size (width/height), just move the center
                const w = (f.box.xmax - f.box.xmin) || 10;
                const h = (f.box.ymax - f.box.ymin) || 10;
                return {
                  ...f,
                  box: {
                    xmin: perX - (w/2),
                    xmax: perX + (w/2),
                    ymin: perY - (h/2),
                    ymax: perY + (h/2)
                  }
                };
              }
              return f;
            })
          };
        }
        return img;
      });
      onUpdate(updated);
    };

    const onMouseUp = () => {
      setIsDragging(false);
      setDraggedFindingId(null);
      setDraggedImageId(null);
    };

    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, draggedImageId, draggedFindingId, images, onUpdate]);


  // --- RENDER ---
  const selectedImage = images.find(i => i.id === selectedImageId);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* GLOBAL HEADER */}
      <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
             <ImageIcon className="w-5 h-5 text-blue-600" />
             Bild- & Risikoanalyse
          </h2>
          <p className="text-sm text-slate-500">
             Laden Sie Bilder hoch. Verschieben Sie die Pins per Drag & Drop.
          </p>
        </div>
        <div>
            <button 
               onClick={() => fileInputRef.current?.click()}
               className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 shadow"
            >
               <ImagePlus className="w-4 h-4" /> Bilder hinzufügen
            </button>
            <input 
               type="file" 
               multiple 
               accept="image/*" 
               ref={fileInputRef} 
               className="hidden" 
               onChange={handleFileSelect} 
            />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-280px)] min-h-[600px]">
          
          {/* LEFT SIDEBAR: LIST OF IMAGES */}
          <div className="lg:col-span-3 flex flex-col gap-3 overflow-y-auto pr-2 pb-10">
              {images.length === 0 && (
                  <div className="text-center p-8 border-2 border-dashed border-slate-200 rounded-xl text-slate-400">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">Keine Bilder vorhanden</p>
                  </div>
              )}
              
              {images.map((img, idx) => (
                  <div 
                    key={img.id}
                    onClick={() => setSelectedImageId(img.id)}
                    className={`
                        relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all p-1
                        ${selectedImageId === img.id ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-slate-100 bg-white hover:border-blue-300'}
                    `}
                  >
                      <div className="flex gap-3 items-center">
                          <div className="w-20 h-20 flex-shrink-0 bg-slate-900 rounded-lg overflow-hidden relative">
                              <img src={img.imageData} className="w-full h-full object-cover" />
                              <div className="absolute bottom-0 right-0 bg-black/60 text-white text-[11px] px-1 rounded-tl">
                                 #{idx + 1}
                              </div>
                          </div>
                          <div className="flex-1 min-w-0 pr-2">
                              <div className="text-xs font-bold text-slate-700 mb-1 truncate">
                                  Bild {idx + 1}
                              </div>
                              <div className="flex items-center gap-1">
                                  {img.findings.length > 0 ? (
                                      <span className="text-[11px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">
                                          {img.findings.length} Gefahren
                                      </span>
                                  ) : (
                                      <span className="text-[11px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                                          Keine Marker
                                      </span>
                                  )}
                              </div>
                              {img.suggestions && img.suggestions.length > 0 && (
                                  <div className="mt-1 flex items-center gap-1 text-[10px] text-blue-600 font-bold">
                                      <Sparkles className="w-3 h-3" /> {img.suggestions.length} Vorschläge
                                  </div>
                              )}
                          </div>
                          <button 
                            onClick={(e) => handleDeleteImage(e, img.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          >
                             <Trash2 className="w-4 h-4" />
                          </button>
                      </div>
                  </div>
              ))}
          </div>

          {/* RIGHT SIDE: EDITOR */}
          <div data-glass="karte" className="lg:col-span-9 bg-white border border-slate-200 rounded-xl shadow-lg flex flex-col h-full overflow-hidden">
              
              {selectedImage ? (
                  <div className="flex flex-col h-full overflow-y-auto">
                      
                      {/* TOOLBAR */}
                      <div className="p-3 border-b border-slate-200 flex justify-between items-center bg-slate-50 sticky top-0 z-20">
                          <span className="font-bold text-slate-700 flex items-center gap-2">
                             <span className="bg-slate-200 text-slate-600 px-2 rounded text-xs">#{images.findIndex(i => i.id === selectedImage.id) + 1}</span> 
                             Editor
                          </span>
                          <div className="flex gap-2 items-center">
                              <select
                                  value={selectedImage.size || 'medium'}
                                  onChange={(e) => {
                                      const updated = images.map(img => img.id === selectedImage.id ? { ...img, size: e.target.value as 'small' | 'medium' | 'large' } : img);
                                      onUpdate(updated);
                                  }}
                                  className="text-xs border border-slate-300 rounded px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  title="Bildgröße im PDF"
                              >
                                  <option value="small">Bild: Klein</option>
                                  <option value="medium">Bild: Mittel</option>
                                  <option value="large">Bild: Groß</option>
                              </select>
                              {selectedImage.findings.length === 0 && !analyzingId && (
                                  <button 
                                    onClick={() => handleAiAnalysis(selectedImage)}
                                    className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-blue-700 shadow-sm transition-colors"
                                  >
                                      <Sparkles className="w-3 h-3" /> KI-Analyse
                                  </button>
                              )}
                              <button 
                                onClick={() => handleAddManualFinding(selectedImage.id)}
                                className="text-xs bg-white border border-slate-300 text-slate-700 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-slate-50"
                              >
                                  <PlusCircle className="w-3 h-3" /> Marker setzen
                              </button>
                          </div>
                      </div>

                      <div className="flex-1 p-6 space-y-8">
                         {/* CANVAS */}
                         <div className="flex justify-center">
                            <div 
                                ref={(el) => { imageRefs.current[selectedImage.id] = el; }}
                                className="relative inline-block z-0 shadow-2xl rounded-lg overflow-hidden bg-slate-900"
                            >
                                <img 
                                    src={selectedImage.imageData} 
                                    alt="Editor" 
                                    className="block max-h-[50vh] w-auto h-auto object-contain pointer-events-none select-none" 
                                />

                                {/* MARKERS */}
                                {selectedImage.findings.map((f, i) => {
                                    let x = 50, y = 50;
                                    if (f.box) {
                                        const xmin = Number(f.box.xmin);
                                        const xmax = Number(f.box.xmax);
                                        const ymin = Number(f.box.ymin);
                                        const ymax = Number(f.box.ymax);
                                        if (!isNaN(xmin) && !isNaN(xmax)) x = (xmin + xmax) / 2;
                                        if (!isNaN(ymin) && !isNaN(ymax)) y = (ymin + ymax) / 2;
                                    }
                                    const isActive = activeFindingId === f.id;

                                    return (
                                        <div
                                        key={f.id}
                                        onMouseDown={(e) => startDrag(e, selectedImage.id, f.id)}
                                        onClick={(e) => { e.stopPropagation(); setActiveFindingId(f.id); }}
                                        className={`absolute z-50 cursor-move transform -translate-x-1/2 -translate-y-1/2 transition-transform duration-75 ${isActive ? 'scale-125 z-[60]' : 'hover:scale-110'}`}
                                        style={{ left: `${x}%`, top: `${y}%` }}
                                        >
                                            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-sm shadow-[0_2px_5px_rgba(0,0,0,0.5)] select-none ${isActive ? 'bg-blue-600 border-white text-white' : 'bg-red-600 border-white text-white'}`}>
                                                {i + 1}
                                            </div>
                                            {isActive && (
                                                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded backdrop-blur-sm pointer-events-none whitespace-nowrap flex items-center gap-1">
                                                    <Move className="w-3 h-3" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                
                                {analyzingId === selectedImage.id && (
                                    <div className="absolute inset-0 bg-slate-900/70 flex flex-col items-center justify-center text-white z-[100] backdrop-blur-sm">
                                        <Loader2 className="w-10 h-10 animate-spin mb-3 text-blue-400" />
                                        <span className="font-bold tracking-wide">Analysiere Bild...</span>
                                    </div>
                                )}
                            </div>
                         </div>

                         {/* EDIT FORM LIST */}
                         <div className="max-w-3xl mx-auto space-y-6">
                             <div className="border-b border-slate-100 pb-2 mb-4">
                                <h3 className="font-bold text-slate-700 flex items-center gap-2">
                                    <Target className="w-4 h-4 text-blue-600" />
                                    Gefährdungsbeurteilung
                                </h3>
                             </div>
                             
                             {selectedImage.findings.length > 0 ? (
                                <div className="grid gap-3">
                                    {selectedImage.findings.map((f, i) => (
                                        <div 
                                        key={f.id} 
                                        onClick={() => setActiveFindingId(f.id)}
                                        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${activeFindingId === f.id ? 'border-blue-400 bg-blue-50/50 shadow-sm ring-1 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}
                                        >
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1 ${activeFindingId === f.id ? 'bg-blue-600 text-white' : 'bg-red-600 text-white'}`}>
                                                {i + 1}
                                            </div>
                                            <div className="flex-1 space-y-3">
                                                <input 
                                                    value={f.title}
                                                    onChange={(e) => handleUpdateFindingText(selectedImage.id, f.id, 'title', e.target.value)}
                                                    className="w-full font-bold text-slate-800 bg-transparent border-b border-transparent focus:border-blue-300 p-1 -ml-1 focus:bg-white outline-none transition-all placeholder:text-slate-300"
                                                    placeholder="Gefährdung Titel..."
                                                />
                                                <textarea 
                                                    value={f.description}
                                                    onChange={(e) => handleUpdateFindingText(selectedImage.id, f.id, 'description', e.target.value)}
                                                    className="w-full text-sm text-slate-600 bg-transparent border border-transparent focus:border-blue-300 focus:bg-white rounded p-2 -ml-2 resize-none placeholder:text-slate-300 outline-none transition-all"
                                                    rows={2}
                                                    placeholder="Beschreibung und Maßnahmen..."
                                                />
                                                
                                                {/* AI ANALYZE BUTTON FOR SPECIFIC FINDING */}
                                                {f.title === "" && f.description === "" && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleAnalyzeSpot(selectedImage, f); }}
                                                        disabled={analyzingFindingId !== null}
                                                        className="flex items-center gap-2 text-xs font-bold text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 transition-colors w-max"
                                                    >
                                                        {analyzingFindingId === f.id ? (
                                                            <>
                                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                                Analysiere Bereich...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Wand2 className="w-3 h-3" />
                                                                Bereich um Marker analysieren
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleDeleteFinding(selectedImage.id, f.id); }}
                                                className="text-slate-300 hover:text-red-500 p-2 rounded hover:bg-red-50 transition-colors"
                                                title="Eintrag löschen"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                             ) : (
                                <div className="text-center py-8 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                                    <p className="text-slate-400 text-sm">Keine Gefahren markiert.</p>
                                </div>
                             )}

                             {/* SUGGESTIONS SECTION */}
                             {selectedImage.suggestions && selectedImage.suggestions.length > 0 && (
                                <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4 mt-6">
                                    <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                                        <Sparkles className="w-4 h-4" /> KI-Vorschläge für Katalog
                                    </h4>
                                    <div className="space-y-2">
                                        {selectedImage.suggestions.map((sug, i) => (
                                        <div key={i} className="bg-white border border-blue-100 rounded-lg p-3 shadow-sm flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
                                            <div>
                                                <div className="font-bold text-slate-800 text-sm">{sug.component}</div>
                                                <div className="text-xs text-slate-500 italic mt-0.5">{sug.reason}</div>
                                            </div>
                                            <div className="flex gap-2 flex-shrink-0">
                                                <button 
                                                    onClick={() => handleAcceptSuggestion(selectedImage.id, sug, i)}
                                                    className="bg-green-600 text-white px-2.5 py-1.5 rounded text-xs font-bold hover:bg-green-700 flex items-center gap-1 shadow-sm"
                                                >
                                                    <Check className="w-3 h-3" /> Übernehmen
                                                </button>
                                                <button 
                                                    onClick={() => handleIgnoreSuggestion(selectedImage.id, i)}
                                                    className="bg-white border border-slate-200 text-slate-500 px-2.5 py-1.5 rounded text-xs font-bold hover:bg-slate-50"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>
                                        ))}
                                    </div>
                                </div>
                             )}
                         </div>
                      </div>
                  </div>
              ) : (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <LayoutGrid className="w-12 h-12 mb-4 opacity-20" />
                      <p>Wählen Sie ein Bild aus oder laden Sie neue hoch.</p>
                  </div>
              )}
          </div>
      </div>
    </div>
  );
};
